import React, { useState, useEffect, useRef } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { pushSupported, isPushEnabled, enablePush } from "../lib/push";

const SNOOZE_KEY = "kora_notif_prompt_snooze";
const SNOOZE_MS = 24 * 3600 * 1000;

export const PushPrompt = () => {
  const { user } = useAuth();
  const { counts } = useNotifications();
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const prev = useRef(0);

  useEffect(() => {
    const cur = counts.messages || 0;
    const increased = cur > prev.current;
    prev.current = cur;
    if (!user || !increased || !pushSupported()) return;
    if (Notification.permission === "denied") return;
    const snoozedAt = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
    if (Date.now() - snoozedAt < SNOOZE_MS) return;
    isPushEnabled().then((on) => { if (!on) setVisible(true); });
  }, [counts.messages, user]);

  const activate = async () => {
    setEnabling(true);
    try {
      await enablePush();
      localStorage.setItem(SNOOZE_KEY, String(Date.now()));
      setVisible(false);
      toast.success("Notifications activées ! Vous serez averti à chaque nouveau message 🎉");
    } catch (e) {
      toast.error(e.message === "denied" ? "Vous avez refusé les notifications dans votre navigateur." : "Impossible d'activer les notifications sur cet appareil.");
    }
    setEnabling(false);
  };

  const dismiss = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-20 sm:bottom-6 z-[60] flex justify-center px-4 pointer-events-none">
      <div data-testid="push-prompt" className="pointer-events-auto w-full max-w-md bg-gradient-to-br from-kora-terracotta to-[#a83a1a] text-white rounded-2xl shadow-[0_14px_40px_rgba(224,93,54,0.5)] border-2 border-kora-ochre p-4 animate-kora-pulse">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
            <Bell size={20} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black text-sm">💬 Vous avez reçu un nouveau message !</p>
            <p className="text-[13px] text-white/90 mt-1 leading-snug">Activez les notifications pour être averti immédiatement lorsque quelqu'un vous écrit.</p>
          </div>
          <button data-testid="push-prompt-close" onClick={dismiss} aria-label="Fermer" className="text-white/70 hover:text-white shrink-0 -mt-1 -mr-1 p-1">
            <X size={18} />
          </button>
        </div>
        <button
          data-testid="push-prompt-enable-btn"
          onClick={activate}
          disabled={enabling}
          className="mt-3 w-full bg-white text-kora-terracotta rounded-full py-2.5 font-bold text-sm hover:bg-kora-sand transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {enabling ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />}
          Activer les notifications
        </button>
      </div>
    </div>
  );
};

export default PushPrompt;
